import { useEffect, useReducer, useState } from 'react';

import { Alert, AlertDescription } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'components/ui/dialog';

// Components
import MainAppBar from 'components/MainAppBar';
import EventHeader from 'components/EventHeader';
import Checkout from 'components/Checkout';
import PurchaseSuccess from 'components/PurchaseSuccess';
import FullScreenModal from 'components/FullScreenModal';
import AccessCodeForm from 'components/AccessCodeForm';
import SeatmapPreviewModal from 'components/SeatmapPreviewModal';

// Utility
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import * as GA4 from 'analytics/ga4';
import { CheckoutProvider, useCheckoutContext } from 'context/CheckoutContext';
import { useStorefrontContext } from 'context/StorefrontContext';
import { useEventContext } from 'context/EventContext';
import { formatEventDateRange } from 'utility/dateFormatting';
import { createMapsURL } from 'utility/maps';

// CheckoutModal wrapper that uses checkout context for step info
function CheckoutModal({ open, onClose }) {
    const {
        stepInfo,
        handleBack,
        handleContinue,
        showControlBar,
        showReviewCart,
        showBackButton,
        showContinueButton,
        formattedTimer,
        releaseHoldToken,
        event,
        receiptData,
        products,
        bundles,
        seatsPicked,
        sessionExpiredDialogOpen,
        handleSessionExpiredDialogClose,
    } = useCheckoutContext();

    const [reviewCartOpen, setReviewCartOpen] = useState(false);

    const handleReviewCart = () => {
        setReviewCartOpen(true);
    };

    const handleReviewCartClose = () => {
        setReviewCartOpen(false);
    };

    const handleReviewCartContinue = () => {
        setReviewCartOpen(false);
        // Navigate to next step (Tickets)
        handleContinue();
    };

    // Clear all seat selections, release hold token, and restart session (modal stays open)
    const handleClearSelection = async () => {
        // Release the current hold token on the server (pass true to indicate intentional clear)
        await releaseHoldToken(true);

        // Clear seats.io session from sessionStorage to force a fresh session
        Object.keys(sessionStorage).forEach((key) => {
            if (key.startsWith('seatsio')) {
                sessionStorage.removeItem(key);
            }
        });

        // Close the review cart popup (the main checkout modal stays open)
        // The SeatsPicker will remount with a fresh session and new timer
        setReviewCartOpen(false);
    };

    // Handle close with hold token release
    const handleClose = async () => {
        // Release holds on seats.io server before closing
        await releaseHoldToken();
        onClose();
    };

    // For non-seats events, disable continue if no items in cart
    const hasItemsInCart = products.some((p) => p.qty > 0) || bundles.some((b) => b.qty > 0);
    const disableContinue = !event.seatsEnabled && !hasItemsInCart;

    return (
        <>
            <FullScreenModal
                open={open}
                onClose={handleClose}
                currentStep={stepInfo.currentStep}
                totalSteps={stepInfo.totalSteps}
                stepLabel={stepInfo.stepLabel}
                subtotal={receiptData?.subTotal || 0}
                seatsPickedCount={seatsPicked.length}
                showControlBar={showControlBar}
                showReviewCart={showReviewCart}
                showBackButton={showBackButton}
                showContinueButton={showContinueButton}
                disableContinue={disableContinue}
                showConfirmOnClose={event.seatsEnabled}
                onBack={handleBack}
                onContinue={handleContinue}
                onReviewCart={handleReviewCart}
                timer={formattedTimer}
                currency={event.currency || 'CAD'}
            >
                <Checkout
                    reviewCartOpen={reviewCartOpen}
                    onReviewCartClose={handleReviewCartClose}
                    onReviewCartContinue={handleReviewCartContinue}
                    onStartOver={handleClearSelection}
                />
            </FullScreenModal>

            <Dialog open={sessionExpiredDialogOpen}>
                <DialogContent
                    className="sm:max-w-md bg-card shadow-xl"
                    onPointerDownOutside={(e) => e.preventDefault()}
                    onEscapeKeyDown={(e) => e.preventDefault()}
                >
                    <DialogHeader>
                        <DialogTitle id="session-expired-dialog-title">
                            <Trans>Your session has expired</Trans>
                        </DialogTitle>
                        <DialogDescription>
                            <Trans>
                                Your reservation window has ended, and the seats you selected are
                                now available to the public. Please start a new selection.
                            </Trans>
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button type="button" onClick={handleSessionExpiredDialogClose}>
                            <Trans>Start Over</Trans>
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function reducer(state, action) {
    switch (action.type) {
        case 'setCode':
            return {
                ...state,
                code: action.code.replace(/ /g, ''),
                status: 'pending',
                message: null,
            };

        case 'deleteCode':
            return { ...state, status: 'pending', code: '', message: null };

        case 'setStatus':
            return { ...state, status: action.status, message: action.message };

        default:
            throw new Error();
    }
}

export default function Storefront() {
    const { event, products, bundles, accessCodesEnabled, accessCodes, handleAccessCodeSubmit } =
        useEventContext();

    const {
        state,
        productCart = [],
        bundleCart = [],
        handleAddProduct,
        handleRemoveProduct,
        handleAddBundle,
        handleRemoveBundle,
        discount,
        setDiscount,
        handleCheckoutOpen,
        handleCheckoutClose,
        handlePurchase,
    } = useStorefrontContext();

    // Discount Code Form State
    const [discountFormState, discountDispatch] = useReducer(reducer, {
        status: 'pending', // pending, error, loading, success
        message: null,
        code: '',
    });

    const { i18n } = useLingui();

    // Reset scroll position to top after component renders
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Analytics - Log page views
    useEffect(() => {
        // Hardcode the path since users still get here from the old URL scheme where event id is passed in via query parameter on the root url. (Ex: https://frontdoor.plus/?e=123)
        switch (state) {
            case 'products':
                GA4.pageView(`Event: ${event.title}`, `/event/${event.eventId}`);
                break;
            case 'checkout':
                GA4.pageView(`Checkout: ${event.title}`, `/event/${event.eventId}`);
                break;
            case 'confirmation':
                GA4.pageView(`Purchase: ${event.title}`, `/event/${event.eventId}`);
                break;
            default:
                break;
        }
    }, [state, event]);

    const [previewOpen, setPreviewOpen] = useState(false);

    const seatSalesUnavailable =
        event.seatsEnabled &&
        (Boolean(event.seatsClosedAt) ||
            (event.parentMode &&
                (!event.seatsSeasonEnabled ||
                    !event.seatsEventKey ||
                    event.seasonAdmissionRemaining === 0)));
    let ticketsAvailable =
        !seatSalesUnavailable &&
        event.concluded === 0 &&
        (products.length > 0 || bundles.length > 0);
    let seatPreviewAvailable =
        !seatSalesUnavailable && event.concluded === 0 && event.seatsEnabled && !ticketsAvailable;

    const seatTickets = products.filter((p) => p.type === 3);

    // Seating events with no seat tickets don't allow customers to checkout, even if there are addons available
    if (
        !seatSalesUnavailable &&
        event.concluded === 0 &&
        event.seatsEnabled &&
        seatTickets.length === 0
    ) {
        ticketsAvailable = false;
        seatPreviewAvailable = true;
    }

    // All available seat products are passed to the storefront. SeatsIO renderer handles the seat availability on the next screen
    // TODO: If we can find a way to tell if all seats for a product are sold out, we can show the sold out button here before rendering the seating chart
    const seatsAvailable = seatTickets.length > 0;
    // Available products+bundles are passed to the storefront, but we have to check if they still have enough stock.
    const productsAvailable =
        products.length > 0 &&
        products.some((p) => p.available > 0 && p.minPurchase <= p.available);
    const bundlesAvailable =
        bundles.length > 0 &&
        bundles.some(
            (b) => b.available > 0 && b.minPurchase <= b.available && b.bundleProductsAvailable > 0,
        );
    const soldOut = ticketsAvailable && !seatsAvailable && !productsAvailable && !bundlesAvailable;

    const dateString = formatEventDateRange(event.startDate, event.endDate, i18n.locale);

    if (state === 'confirmation') {
        return <PurchaseSuccess />;
    }

    let checkoutLabel = event.parentMode ? t`Get Season Tickets` : t`Get Tickets`;

    if (seatSalesUnavailable) {
        checkoutLabel = t`Unavailable`;
    } else if (seatPreviewAvailable) {
        checkoutLabel = t`Preview Seatmap`;
    } else if (soldOut) {
        checkoutLabel = t`Sold Out`;
    }

    return (
        <div className="min-h-full bg-background text-foreground">
            <MainAppBar
                onClick={seatPreviewAvailable ? () => setPreviewOpen(true) : handleCheckoutOpen}
                disabled={
                    soldOut ||
                    event.chargesEnabled === 0 ||
                    (!ticketsAvailable && !seatPreviewAvailable)
                }
                label={checkoutLabel}
                sticky={false}
            />

            <EventAlert
                seatSalesUnavailable={seatSalesUnavailable}
                parentMode={event.parentMode}
                chargesEnabled={event.chargesEnabled}
                concluded={event.concluded}
                products={products}
                bundles={bundles}
            />

            <EventHeader
                title={event.title}
                parentMode={event.parentMode}
                eventId={event.eventId}
                hostName={event.hostName}
                hostId={event.hostId}
                ticketsAvailable={ticketsAvailable}
                seatPreviewAvailable={seatPreviewAvailable}
                soldOut={soldOut}
                chargesEnabled={event.chargesEnabled}
                checkoutLabel={checkoutLabel}
                onGetTickets={handleCheckoutOpen}
                onPreviewSeatmap={() => setPreviewOpen(true)}
                date={dateString}
                concluded={event.concluded}
                venue={event.venue}
                address={`${event.address} - ${event.city}, ${event.province}`}
                mapsURL={createMapsURL(event.address, event.city, event.province, event.postalCode)}
                description={event.description}
                descriptionHtml={event.descriptionHtml}
                imageUrl={event.imageUrl || undefined}
                websiteUrl={event.websiteUrl}
                facebookUrl={event.facebookUrl}
                twitterUrl={event.twitterUrl}
                instagramUrl={event.instagramUrl}
                tiktokUrl={event.tiktokUrl}
                linkedinUrl={event.linkedinUrl}
                youtubeUrl={event.youtubeUrl}
            />

            {accessCodesEnabled === true && (
                <AccessCodeForm
                    eventId={event.eventId}
                    accessCodes={accessCodes}
                    onSubmit={handleAccessCodeSubmit}
                />
            )}

            {seatPreviewAvailable && (
                <SeatmapPreviewModal
                    open={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                    event={event}
                />
            )}

            {state === 'checkout' && !seatSalesUnavailable && (
                <CheckoutProvider
                    products={productCart}
                    bundles={bundleCart}
                    handleAddProduct={handleAddProduct}
                    handleRemoveProduct={handleRemoveProduct}
                    handleAddBundle={handleAddBundle}
                    handleRemoveBundle={handleRemoveBundle}
                    event={event}
                    discount={discount}
                    discountFormState={discountFormState}
                    discountDispatch={discountDispatch}
                    onDiscountChange={setDiscount}
                    accessCodes={accessCodes}
                    onPurchase={handlePurchase}
                    onCancel={handleCheckoutClose}
                >
                    <CheckoutModal open={state === 'checkout'} onClose={handleCheckoutClose} />
                </CheckoutProvider>
            )}
        </div>
    );
}
function EventAlert(props) {
    const { chargesEnabled, concluded, products, bundles, seatSalesUnavailable, parentMode } =
        props;

    const wrap = (child) => <div className="px-4 pt-4 md:px-8 md:pt-8">{child}</div>;

    if (seatSalesUnavailable) {
        return wrap(
            <Alert variant="info" className="[&>svg~*]:pl-0">
                <AlertDescription>
                    {parentMode ? (
                        <Trans>Season tickets are not available for purchase.</Trans>
                    ) : (
                        <Trans>Tickets are no longer available for this event.</Trans>
                    )}
                </AlertDescription>
            </Alert>,
        );
    }

    if (concluded === 1) {
        return wrap(
            <Alert variant="warning" className="[&>svg~*]:pl-0">
                <AlertDescription>
                    <Trans>The event is over, see you again soon.</Trans>
                </AlertDescription>
            </Alert>,
        );
    }

    if (chargesEnabled === 0) {
        return wrap(
            <Alert variant="info" className="[&>svg~*]:pl-0">
                <AlertDescription>
                    <Trans>Tickets will be available for sale soon. Please check back later.</Trans>
                </AlertDescription>
            </Alert>,
        );
    }

    if (products.length === 0 && bundles.length === 0) {
        return wrap(
            <Alert variant="info" className="[&>svg~*]:pl-0">
                <AlertDescription>
                    <Trans>Tickets aren't available to purchase at the moment.</Trans>
                </AlertDescription>
            </Alert>,
        );
    }

    return null;
}
